import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeneralEnquiryForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    affiliation_organisation: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("contact_name", formData.contact_name);
      formDataToSend.append("contact_email", formData.contact_email);
      if (formData.affiliation_organisation) {
        formDataToSend.append("affiliation_organisation", formData.affiliation_organisation);
      }
      formDataToSend.append("message", formData.message);

      const response = await axios.post(`${API}/submissions/general-enquiry`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Check if response is successful
      if (response.status === 200 || response.status === 201) {
        toast.success("General enquiry submitted successfully! We will get back to you soon.");
        
        // Reset form
        setFormData({
          contact_name: "",
          contact_email: "",
          affiliation_organisation: "",
          message: "",
        });
        
        // Navigate back to home after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (error) {
      console.error("Submission error:", error);
      // Check if submission actually succeeded but response was blocked
      if (error.response?.status === 200 || error.response?.status === 201) {
        toast.success("Enquiry submitted successfully!");
        setFormData({
          contact_name: "",
          contact_email: "",
          affiliation_organisation: "",
          message: "",
        });
        setTimeout(() => navigate("/"), 2000);
      } else {
        toast.error("Failed to submit enquiry. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
              <MessageSquare className="h-6 w-6 text-indigo-600" />
            </div>
            <CardTitle className="text-2xl" >
              General Enquiry
            </CardTitle>
            <CardDescription>
              Have a question or need more information? Send us your enquiry and we'll get back to you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="contact_name">Name *</Label>
                <Input
                  id="contact_name"
                  data-testid="contact-name-input"
                  required
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Enter your full name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact_email">Email Address *</Label>
                <Input
                  id="contact_email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="affiliation_organisation">Affiliation / Organisation (Optional)</Label>
                <Input
                  id="affiliation_organisation"
                  data-testid="affiliation-organisation-input"
                  value={formData.affiliation_organisation}
                  onChange={(e) => setFormData({ ...formData, affiliation_organisation: e.target.value })}
                  placeholder="University or organization name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  data-testid="message-textarea"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Please describe your enquiry in detail"
                  rows={6}
                  className="mt-2"
                />
              </div>

              <div className="flex space-x-4">
                <Button
                  type="submit"
                  data-testid="submit-enquiry-btn"
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700 flex-1"
                >
                  {submitting ? "Submitting..." : "Submit Enquiry"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GeneralEnquiryForm;
